import { useState } from 'react';
import { Card } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Briefcase, Search, Share2, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useNetworkProfessionals } from './hooks/useNetworkProviders';
import { getFileUrl } from '@/shared/hooks/useFileUpload';
import { ReferralModal } from './components/ReferralModal';
import { NetworkMessageButton } from './components/NetworkMessageButton';
// import type { NetworkProvider } from './api/networkApi';
import type { NetworkProfessional } from './api/networkApi';

/**
 * Converts S3 file key to full URL if needed
 * Returns null for empty/falsy values, or the full URL
 */
const getProfilePhotoUrl = (photo: string | null): string | null => {
  if (!photo || photo.trim() === '') {
    return null;
  }
  
  // If already a full URL, return as is
  if (photo.startsWith('http://') || photo.startsWith('https://')) {
    return photo;
  }
  
  // Convert S3 file key to full URL
  return getFileUrl(photo);
};

/**
 * Get display label for professional role
 */
const getRoleLabel = (role: NetworkProfessional['role']): string => {
  return role === 'SERVICE_PROVIDER' ? 'Service Provider' : 'Financial Advisor';
};

/**
 * Get badge styling based on professional role
 */
const getRoleBadgeClass = (role: NetworkProfessional['role']): string => {
  return role === 'SERVICE_PROVIDER'
    ? 'bg-[#026857]/10 text-[#026857] hover:bg-[#026857]/20 border-[#026857]/20'
    : 'bg-[#378F94]/10 text-[#378F94] hover:bg-[#378F94]/20 border-[#378F94]/20';
};

type MyNetworkSectionProps = {
  onFindProvidersClick?: () => void;
};

export function MyNetworkSection({
  onFindProvidersClick,
}: MyNetworkSectionProps) {
  const navigate = useNavigate();
  const { data: response, isLoading, isError, refetch } = useNetworkProfessionals();
  const [referralModalOpen, setReferralModalOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<{ id: string; name: string } | null>(
    null,
  );

  /**
   * Navigate to the correct profile page based on professional role
   * - Service Providers: /providers/:providerId (actual service provider ID)
   * - Financial Advisors: /advisors/:id (uses targetUserId for user-based routing)
   */
  const handleProfessionalClick = (professional: NetworkProfessional) => {
    if (professional.role === 'SERVICE_PROVIDER') {
      // Use providerId (actual service provider ID) instead of id (network connection ID)
      if (!professional.providerId) {
        console.error('No provider ID available for navigation');
        return;
      }
      navigate(`/providers/${professional.providerId}`);
    } else {
      // Financial Advisors use user-based routing
      navigate(`/advisors/${professional.targetUserId}`);
    }
  };

  // Feature 001: Handle refer provider click
  const handleReferClick = (
    e: React.MouseEvent,
    providerId: string | undefined,
    providerName: string,
  ) => {
    e.stopPropagation(); // Prevent row click
    if (!providerId) {
      console.error('No provider ID available for referral');
      return;
    }
    setSelectedProvider({ id: providerId, name: providerName });
    setReferralModalOpen(true);
  };

  // Skeleton loading state
  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg text-gray-900">My Network</h3>
        </div>
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center gap-3 p-3">
              <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
                <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2" />
              </div>
              <div className="w-8 h-8 bg-gray-200 rounded animate-pulse flex-shrink-0" />
            </div>
          ))}
        </div>
      </Card>
    );
  }

  // Error state with manual retry button
  if (isError) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg text-gray-900">My Network</h3>
        </div>
        <div className="text-center py-8">
          <p className="text-red-600 mb-4">
            Error loading network professionals. Please try again.
          </p>
          <Button onClick={() => refetch()} variant="outline" size="sm">
            Try Again
          </Button>
        </div>
      </Card>
    );
  }

  const professionals = response?.data || [];
  const total = response?.total || 0;
  const hasMore = response?.hasMore || false;
  const professionalCount = professionals.length;
  const hasProfessionals = professionalCount > 0;

  const getInitials = (companyName: string): string => {
    const words = companyName.trim().split(' ');
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    }
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg text-gray-900">My Network</h3>
          {hasProfessionals && (
            <p className="text-sm text-gray-500 mt-1">
              Showing {professionalCount} of {total} {total === 1 ? 'professional' : 'professionals'}
            </p>
          )}
        </div>
        {hasProfessionals && (
          <div className="flex gap-2">
            {hasMore && onFindProvidersClick && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onFindProvidersClick}
                className="text-[#026857] hover:text-[#025247] hover:bg-[#026857]/10"
              >
                View All
              </Button>
            )}
            {onFindProvidersClick && (
              <Button
                variant="outline"
                size="sm"
                onClick={onFindProvidersClick}
                className="gap-2"
              >
                <Search className="w-4 h-4" />
                Find More
              </Button>
            )}
          </div>
        )}
      </div>

      {!hasProfessionals ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-gray-400" />
          </div>
          <h4 className="text-gray-900 mb-2">No professionals in your network yet</h4>
          <p className="text-sm text-gray-500 mb-6">
            Start building your professional network by finding providers and advisors
          </p>
          {onFindProvidersClick && (
            <Button
              onClick={onFindProvidersClick}
              className="bg-[#026857] hover:bg-[#025247] gap-2"
            >
              <Search className="w-4 h-4" />
              Find Professionals
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {professionals.map((professional) => {
            const photoUrl = getProfilePhotoUrl(professional.profilePhoto);
            return (
              <div
                key={professional.id}
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => handleProfessionalClick(professional)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleProfessionalClick(professional);
                  }
                }}
              >
                {photoUrl ? (
                  <img
                    src={photoUrl}
                    alt={professional.companyName}
                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[#378F94]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#378F94] font-semibold text-xs">
                      {getInitials(professional.companyName)}
                    </span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <h4 className="text-sm font-medium text-gray-900 truncate">
                      {professional.companyName}
                    </h4>
                    <Badge 
                      variant="outline" 
                      className={`text-[10px] px-1.5 py-0 h-4 font-normal ${getRoleBadgeClass(professional.role)}`}
                    >
                      {professional.role === 'SERVICE_PROVIDER' ? (
                        <Briefcase className="w-2.5 h-2.5 mr-0.5" />
                      ) : (
                        <Users className="w-2.5 h-2.5 mr-0.5" />
                      )}
                      {getRoleLabel(professional.role)}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-600 truncate">
                    {professional.name}
                  </p>
                  {professional.specialty && (
                    <p className="text-xs text-gray-500 truncate">
                      {professional.specialty}
                    </p>
                  )}
                </div>
                {/* Feature 001: Side-by-side buttons */}
                <div className="flex gap-1 flex-shrink-0">
                  <NetworkMessageButton professional={professional} />
                  {professional.role === 'SERVICE_PROVIDER' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-2 text-gray-600 hover:text-[#026857] hover:bg-[#026857]/10"
                      onClick={(e) =>
                        handleReferClick(e, professional.providerId, professional.companyName)
                      }
                      title="Refer provider to clients"
                    >
                      <Share2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* Feature 001: Referral Modal */}
      <ReferralModal
        open={referralModalOpen}
        onOpenChange={setReferralModalOpen}
        providerId={selectedProvider?.id || null}
        providerName={selectedProvider?.name || ''}
      />
    </Card>
  );
}
